import {
    UseInfiniteQueryOptions,
    UseQueryOptions,
    useQuery,
    useInfiniteQuery,
} from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { IMutualFriends } from 'interfaces'
import { TMutualFriendsProps } from 'services/UserService'
import { UserService } from 'services'
import * as userSelectors from 'containers/User/user-selectors'
import { getRequestError } from 'utils/helpers'

export type TFetchFnParams = TMutualFriendsProps
export type TError = string
export type TQueryKey = [string, TFetchFnParams] | string[]
export type TQueryFnOpts = UseQueryOptions<IMutualFriends[], TError, IMutualFriends, TQueryKey>
export type TInfiniteQueryFnOpts = UseInfiniteQueryOptions<
    IMutualFriends[],
    TError,
    IMutualFriends[],
    IMutualFriends[],
    TQueryKey
>

const key = 'mutual-friends'

/**
 * Хук API получить список общих друзей
 */
export default function useFetchMutualFriends(params: TFetchFnParams, {
    cacheTime = 600 * 1000, // 10m
    staleTime = 600 * 1000, // 10m
    ...opts
}: TQueryFnOpts = {}) {
    const { t } = useTranslation()
    const user = useSelector(userSelectors.user)

    return useQuery([`${key}-${user.id}`, params], () => {
        return UserService.fetchMutualFriends(params)
            .then(({ data }) => {
                return data
            })
            .catch((err) => {
                return Promise.reject(getRequestError(err) || t('update_error'))
            })
    }, {
        cacheTime,
        staleTime,
        ...opts,
    })
}

export function useFetchInfiniteMutualFriends(initParams: TFetchFnParams, {
    cacheTime = 600 * 1000, // 10m
    staleTime = 600 * 1000, // 10m
    ...opts
}: TInfiniteQueryFnOpts = {}) {
    const { t } = useTranslation()
    const user = useSelector(userSelectors.user)

    return useInfiniteQuery([`${key}-${user.id}`, initParams], ({ pageParam = initParams }) => {
        return UserService.fetchMutualFriends(pageParam)
            .then(({ data }) => {
                return data
            })
            .catch((err) => {
                return Promise.reject(getRequestError(err) || t('update_error'))
            })
    }, {
        getNextPageParam: (lastPage, pages) => {
            const listLen = lastPage && lastPage.length ? lastPage[0].friends.length : 0
            return { ...initParams, offset: pages.length * listLen }
        },
        cacheTime,
        staleTime,
        ...opts,
    })
}
